import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import PageHero from '@/components/ui/PageHero'
import SectionHeader from '@/components/ui/SectionHeader'
import ArtifactCard from '@/components/ui/ArtifactCard'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { PodcastSeries, PodcastEpisode } from '@/types'
import * as api from '@/lib/api'

const PER_SERIES = 20

export default function PodcastsPage() {
  // SEO
  useSEO({
    title: 'Archive Podcasts',
    description: 'In-depth audio explorations of historical topics from across the archives. Subscribe on your favorite platform.',
    url: 'https://thearchivenetwork.com/podcasts',
  })
  const [series, setSeries] = useState<PodcastSeries[]>([])
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([])
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [seriesLoading, setSeriesLoading] = useState(true)
  const [episodesLoading, setEpisodesLoading] = useState(true)

  const episodesRef = useRef<HTMLDivElement>(null)
  const seriesFilterRef = useRef<HTMLDivElement>(null)
  // Where to scroll once the new mode has actually rendered. Recorded when the
  // selection changes, consumed once the fetch it triggered has settled.
  const pendingScrollRef = useRef<'episodes' | 'series' | null>(null)
  const mountedRef = useRef(false)

  // Load the series list once.
  useEffect(() => {
    api
      .getPodcastSeries()
      .then(setSeries)
      .catch((e) => console.error('Failed to load series:', e))
      .finally(() => setSeriesLoading(false))
  }, [])

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250)
    return () => clearTimeout(t)
  }, [searchQuery])

  const searching = debouncedQuery.length > 0

  // Load episodes for the current mode:
  //   searching        -> global search across all series (API `q`)
  //   selectedSeries    -> every episode of one series ("view all")
  //   default           -> latest PER_SERIES per series (API `perSeries`), grouped below
  useEffect(() => {
    let cancelled = false
    setEpisodesLoading(true)
    const opts = searching
      ? { q: debouncedQuery, limit: 100 }
      : selectedSeries
        ? { series: selectedSeries }
        : { perSeries: PER_SERIES }
    api
      .getPodcastEpisodes(opts)
      .then((eps) => {
        if (!cancelled) setEpisodes(eps)
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Failed to load episodes:', e)
          setEpisodes([])
        }
      })
      .finally(() => {
        if (!cancelled) setEpisodesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery, selectedSeries, searching])

  // Selecting a series records WHERE to scroll; it does not scroll yet. Changing
  // selectedSeries kicks off a new fetch above, so at this point the episodes
  // still on screen belong to the previous mode — scrolling now would measure the
  // old layout height and land in the wrong place.
  // Deliberately skipped on mount: landing on /podcasts should not move the page.
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    pendingScrollRef.current = selectedSeries ? 'episodes' : 'series'
  }, [selectedSeries])

  // ...and this performs it, once the fetch has settled and the new list is
  // committed to the DOM. Keyed on the loaded data rather than on the selection
  // for exactly that reason.
  useEffect(() => {
    const target = pendingScrollRef.current
    if (!target || episodesLoading) return
    pendingScrollRef.current = null

    const el = target === 'episodes' ? episodesRef.current : seriesFilterRef.current
    if (!el) return

    // Offset for the fixed 64px TopNav lives in scroll-mt-20 on the targets, not
    // in a pixel constant here.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' })
  }, [episodesLoading, episodes])

  // Client-side narrowing as a safety net (correct even if the API ignores `q`).
  const searchResults = useMemo(() => {
    if (!searching) return episodes
    const q = debouncedQuery.toLowerCase()
    return episodes.filter(
      (ep) =>
        ep.title.toLowerCase().includes(q) ||
        ep.summary?.toLowerCase().includes(q)
    )
  }, [episodes, searching, debouncedQuery])

  // Group the default view by series, capping each at PER_SERIES client-side too.
  const grouped = useMemo(() => {
    const map = new Map<string, PodcastEpisode[]>()
    for (const ep of episodes) {
      const arr = map.get(ep.series_id) ?? []
      if (arr.length < PER_SERIES) arr.push(ep)
      map.set(ep.series_id, arr)
    }
    return map
  }, [episodes])

  const openSeries = (id: string) => {
    setSearchQuery('')
    setDebouncedQuery('')
    setSelectedSeries(id)
    // No scroll here: the effect above waits for the new episodes to land first.
  }

  const renderCard = (episode: PodcastEpisode) => {
    const episodeSeries = series.find((s) => s.id === episode.series_id)
    return (
      <ArtifactCard
        key={episode.id}
        title={episode.title}
        description={episode.summary}
        imageUrl={
          episode.cover_image_url ||
          episodeSeries?.cover_image_url ||
          `/images/podcasts/${episode.archive_key}.jpg`
        }
        linkTo={`/podcast/${episode.id}`}
        type="podcast"
        archiveKey={episode.archive_key}
        meta={new Date(episode.published_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      />
    )
  }

  if (seriesLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const selected = selectedSeries ? series.find((s) => s.id === selectedSeries) : null

  return (
    <div className="animate-fade-in">
      <PageHero
        image="/images/covers/podcast.jpg"
        title="Archive Podcasts"
        subtitle="In-depth audio explorations of historical topics from across the archives. Subscribe on your favorite platform."
        variant="banner"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Curator mention */}
        <div className="text-center mb-12">
          <p className="text-sm text-archive-muted/90 max-w-2xl mx-auto leading-relaxed">
            The archive series are narrated by Marcus, Keeper of the Archive. The Dialogues are hosted
            by Aurelius and Malik, who take a single subject and work it through from both sides.{' '}
            <Link to="/curators" className="text-archive-gold hover:text-archive-amber transition-colors">
              Meet the Curators →
            </Link>
          </p>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              placeholder="Search episodes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-12 bg-archive-slate border border-archive-stone/50 rounded-lg text-archive-cream placeholder-archive-muted focus:outline-none focus:border-archive-gold/50 transition-colors"
            />
            <svg
              className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-archive-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-archive-muted hover:text-archive-cream"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Series Filter */}
        {series.length > 0 && (
          <div ref={seriesFilterRef} className="mb-12 scroll-mt-20">
            <SectionHeader title="Podcast Series" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {series.map((s) => (
                <button
                  key={s.id}
                  onClick={() => (selectedSeries === s.id ? setSelectedSeries(null) : openSeries(s.id))}
                  className={`text-left artifact-frame p-4 transition-all ${
                    selectedSeries === s.id
                      ? 'border-archive-gold'
                      : 'hover:border-archive-gold/40'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={s.cover_image_url || '/podcasts/default-cover.jpg'}
                      alt={s.title}
                      className="w-16 h-16 rounded object-cover"
                    />
                    <div>
                      <h3 className="font-display text-archive-cream">{s.title}</h3>
                      <p className="text-sm text-archive-muted line-clamp-1">{s.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Episodes area — the scroll target when a series is opened.
            scroll-mt-20 (80px) clears the fixed 64px TopNav. */}
        <div ref={episodesRef} className="scroll-mt-20">
          {episodesLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : searching ? (
            /* --- Search results (flat, across all series) --- */
            <>
              <SectionHeader
                title="Search Results"
                subtitle={`${searchResults.length} episode${searchResults.length === 1 ? '' : 's'} matching "${debouncedQuery}"`}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {searchResults.map(renderCard)}
              </div>
              {searchResults.length === 0 && (
                <div className="text-center py-12 text-archive-muted">
                  No episodes found for "{debouncedQuery}".{' '}
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-archive-gold hover:text-archive-amber"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </>
          ) : selected ? (
            /* --- Single series: every episode ("view all") --- */
            <>
              <SectionHeader
                title={selected.title}
                subtitle={`All ${episodes.length} episode${episodes.length === 1 ? '' : 's'}`}
              />
              <button
                onClick={() => setSelectedSeries(null)}
                className="mb-6 text-sm text-archive-gold hover:text-archive-amber"
              >
                ← Back to all series
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {episodes.map(renderCard)}
              </div>
              {episodes.length === 0 && (
                <div className="text-center py-12 text-archive-muted">No episodes available yet.</div>
              )}
            </>
          ) : (
            /* --- Default: grouped by series, latest PER_SERIES each --- */
            <div className="space-y-14">
              {series
                .filter((s) => (grouped.get(s.id)?.length ?? 0) > 0)
                .map((s) => {
                  const eps = grouped.get(s.id) ?? []
                  return (
                    <section key={s.id}>
                      <div className="flex items-end justify-between gap-4 mb-4">
                        <h2 className="section-header text-2xl sm:text-3xl">{s.title}</h2>
                        <button
                          onClick={() => openSeries(s.id)}
                          className="shrink-0 text-sm text-archive-gold hover:text-archive-amber whitespace-nowrap"
                        >
                          View all episodes →
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {eps.map(renderCard)}
                      </div>
                    </section>
                  )
                })}
              {episodes.length === 0 && (
                <div className="text-center py-12 text-archive-muted">No episodes available.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
